import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { wikiApi, type WikiCollectionTree } from '@/shared/api';

export type DocNavItem = {
  urlId: string;
  title: string;
};

export type UseDocNavResult = {
  prev: DocNavItem | null;
  next: DocNavItem | null;
  navigatePrev: () => void;
  navigateNext: () => void;
};

export function useDocNav(currentUrlId: string): UseDocNavResult {
  const navigate = useNavigate();
  const { state } = useLocation();

  const { data } = useQuery({
    queryKey: ['wiki', 'collections'],
    queryFn: async (): Promise<WikiCollectionTree[]> => (await wikiApi.collections()).data,
  });

  const items = React.useMemo<DocNavItem[]>(() => {
    const collection = data?.find((c) => c.documents.some((d) => d.urlId === currentUrlId));
    return (collection?.documents ?? []).map((d) => ({ urlId: d.urlId, title: d.title }));
  }, [data, currentUrlId]);

  const { prev, next } = React.useMemo(() => {
    const idx = items.findIndex((item) => item.urlId === currentUrlId);
    if (idx === -1) return { prev: null, next: null };
    return {
      prev: idx > 0 ? items[idx - 1] : null,
      next: idx < items.length - 1 ? items[idx + 1] : null,
    };
  }, [items, currentUrlId]);

  const navigatePrev = React.useCallback(() => {
    if (prev) navigate(`/instructions/${prev.urlId}`, { state });
  }, [prev, navigate, state]);

  const navigateNext = React.useCallback(() => {
    if (next) navigate(`/instructions/${next.urlId}`, { state });
  }, [next, navigate, state]);

  return { prev, next, navigatePrev, navigateNext };
}
